/**
 * ServiceAuthorizationPage — Service/Budget Authorization Tracking
 *
 * Used by: CFSS (service auths), ALF (bed authorizations), Legal (retainer tracking)
 * Shows authorized units, utilization, and expiration.
 *
 * Route: /{industry}/authorizations
 * Scope: authorization:view
 */

import React from 'react';
import {
  Alert,
  Badge,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconCalendar,
  IconCheck,
  IconClock,
  IconFileDescription,
  IconAlertTriangle,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useHasScope } from '@/auth';

const GET_SERVICE_AUTHORIZATIONS = gql`
  query GetServiceAuthorizations($limit: Int) {
    myServiceAuthorizations(limit: $limit) {
      id
      serviceType
      authorizedUnits
      usedUnits
      unitType
      startDate
      endDate
      status
      leadAgency
    }
  }
`;

interface ServiceAuthorization {
  id: string;
  serviceType: string;
  authorizedUnits: number;
  usedUnits: number;
  unitType: string;
  startDate: string;
  endDate: string;
  status: string;
  leadAgency: string | null;
}

interface ServiceAuthorizationPageProps {
  title?: string;
  subtitle?: string;
  serviceLabel?: string;
  agencyLabel?: string;
  scope?: string;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function getDaysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const today = new Date();
  return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case 'active': return 'green';
    case 'pending': return 'yellow';
    case 'expired': return 'red';
    case 'suspended': return 'orange';
    default: return 'gray';
  }
}

export function ServiceAuthorizationPage({
  title = 'Service Authorizations',
  subtitle = 'Approved services and utilization',
  serviceLabel = 'Service',
  agencyLabel = 'Agency',
  scope = 'authorization:view',
}: ServiceAuthorizationPageProps) {
  const canView = useHasScope(scope);

  const { data, loading } = useQuery<{ myServiceAuthorizations: ServiceAuthorization[] }>(
    GET_SERVICE_AUTHORIZATIONS,
    { variables: { limit: 50 } }
  );

  const authorizations = data?.myServiceAuthorizations ?? [];
  const activeAuths = authorizations.filter(a => a.status === 'active');
  const totalAuthorized = activeAuths.reduce((sum, a) => sum + a.authorizedUnits, 0);
  const totalUsed = activeAuths.reduce((sum, a) => sum + a.usedUnits, 0);
  const overallUtilization = totalAuthorized > 0 ? Math.round((totalUsed / totalAuthorized) * 100) : 0;

  const expiringSoon = activeAuths.filter(a => {
    const days = getDaysRemaining(a.endDate);
    return days >= 0 && days <= 30;
  });

  if (loading) {
    return <Center h="60vh"><Loader size="lg" /></Center>;
  }

  if (!canView) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Access Denied" icon={<IconAlertCircle />}>
          You do not have permission to view authorizations.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group gap="xs">
          <ThemeIcon size="lg" variant="light" color="indigo">
            <IconFileDescription size={20} />
          </ThemeIcon>
          <Stack gap={0}>
            <Title order={2}>{title}</Title>
            <Text size="sm" c="dimmed">{subtitle}</Text>
          </Stack>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 4 }}>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Active</Text>
              <ThemeIcon size="sm" variant="light" color="green"><IconCheck size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c="green">{activeAuths.length}</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Total Units</Text>
              <ThemeIcon size="sm" variant="light" color="blue"><IconClock size={14} /></ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{totalAuthorized}</Text>
            <Text size="xs" c="dimmed">{totalUsed} used</Text>
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Utilization</Text>
              <Text size="sm" fw={600} c={overallUtilization > 90 ? 'red' : overallUtilization > 75 ? 'yellow' : 'green'}>
                {overallUtilization}%
              </Text>
            </Group>
            <Progress value={overallUtilization} color={overallUtilization > 90 ? 'red' : 'blue'} size="lg" mt="xs" />
          </Card>
          <Card withBorder padding="lg">
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Expiring Soon</Text>
              <ThemeIcon size="sm" variant="light" color={expiringSoon.length > 0 ? 'orange' : 'gray'}>
                <IconCalendar size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={expiringSoon.length > 0 ? 'orange' : undefined}>{expiringSoon.length}</Text>
          </Card>
        </SimpleGrid>

        {expiringSoon.length > 0 && (
          <Alert color="orange" title="Expiring Soon" icon={<IconAlertTriangle />}>
            {expiringSoon.length} authorization(s) will expire within 30 days.
          </Alert>
        )}

        {authorizations.length === 0 ? (
          <Alert color="blue" title="No Authorizations" icon={<IconFileDescription />}>
            No authorizations found.
          </Alert>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{serviceLabel}</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Period</Table.Th>
                  <Table.Th ta="right">Authorized</Table.Th>
                  <Table.Th ta="right">Used</Table.Th>
                  <Table.Th>Utilization</Table.Th>
                  <Table.Th>{agencyLabel}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {authorizations.map(auth => {
                  const utilization = auth.authorizedUnits > 0 ? Math.round((auth.usedUnits / auth.authorizedUnits) * 100) : 0;
                  const daysRemaining = getDaysRemaining(auth.endDate);
                  return (
                    <Table.Tr key={auth.id}>
                      <Table.Td><Text size="sm" fw={500}>{auth.serviceType}</Text></Table.Td>
                      <Table.Td><Badge color={getStatusColor(auth.status)} size="sm">{auth.status}</Badge></Table.Td>
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="xs">{formatDate(auth.startDate)} - {formatDate(auth.endDate)}</Text>
                          {auth.status === 'active' && daysRemaining <= 30 && daysRemaining >= 0 && (
                            <Text size="xs" c="orange">{daysRemaining} days left</Text>
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td ta="right"><Text size="sm">{auth.authorizedUnits} {auth.unitType}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="sm">{auth.usedUnits} {auth.unitType}</Text></Table.Td>
                      <Table.Td>
                        <Tooltip label={`${auth.usedUnits} of ${auth.authorizedUnits}`}>
                          <Stack gap={2}>
                            <Progress value={Math.min(utilization, 100)} color={utilization > 100 ? 'red' : 'blue'} size="sm" />
                            <Text size="xs" c="dimmed">{utilization}%</Text>
                          </Stack>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td><Text size="sm" c="dimmed">{auth.leadAgency ?? '—'}</Text></Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

export default ServiceAuthorizationPage;
